export const ROLES = {
  SUPER_ADMIN: "super_admin",
  SUPPORT_ADMIN: "support_admin",
  SUPPORT_ENGINEER: "support_engineer",
  TEST_USER: "test_user",
  PR_ADMIN: "pr_admin",
  BUSINESS_ADMIN: "business_admin",
  CLIENT_ADMIN: "client_admin",
  CLIENT_USER: "client_user",
  BUSINESS_USER: "business_user",
};

// These permissions are required to keep the ticket workflow usable. The
// backend applies the same grants, so a stale session permission snapshot
// must not hide the corresponding workflow actions in the UI.
const REQUIRED_WORKFLOW_PERMISSIONS = {
  [ROLES.SUPPORT_ADMIN]: ["validate_tickets", "manage_pr", "assign_engineers"],
  [ROLES.SUPPORT_ENGINEER]: ["manage_pr", "assign_engineers"],
  [ROLES.BUSINESS_ADMIN]: ["validate_tickets", "assign_engineers"],
  [ROLES.TEST_USER]: ["validate_tickets", "assign_engineers"],
};

// Mirrors the effective Roles & Permissions matrix returned with the signed-in
// user. The backend enforces permissions; this only hides unavailable actions.
export const hasPermission = (user, permission) =>
  user?.role === ROLES.SUPER_ADMIN ||
  (REQUIRED_WORKFLOW_PERMISSIONS[user?.role] || []).includes(permission) ||
  (user?.permissions || []).includes(permission);

export const ROLE_LABEL = {
  super_admin: "Super Admin",
  support_admin: "Support Admin",
  support_engineer: "Support Engineer",
  test_user: "Test User",
  pr_admin: "PR Admin",
  business_admin: "Business Admin",
  client_admin: "Client Admin",
  client_user: "Client User",
  business_user: "Business User",
};

export const isClientStatusRole = (role) =>
  [ROLES.CLIENT_ADMIN, ROLES.CLIENT_USER, ROLES.BUSINESS_USER].includes(role);

export const PRIORITIES = ["P1", "P2", "P3", "P4", "P5"];
export const PRIORITY_LABEL = {
  P1: "P1 — Immediate",
  P2: "P2 — High",
  P3: "P3 — Normal",
  P4: "P4 — Low",
  P5: "P5 — Planned",
};

export const SEVERITIES = ["critical", "major", "moderate", "minor"];

export const TICKET_TYPES = [
  "incident",
  "bug",
  "task",
  "service_request",
  "change_request",
  "enhancement",
  "access_request",
  "data_request",
  "training_request",
  "infrastructure_issue",
  "security_issue",
];

export const STATUS_GROUPS = {
  intake: ["open"],
  assigned: ["assigned"],
  working: ["in_progress", "dev_done", "dev_testing", "in_qa", "reopened"],
  waiting: ["client_confirmation_pending", "reference_pending", "on_hold"],
  resolution: ["resolved", "completed", "invalid"],
};

export const ALL_STATUSES = Object.values(STATUS_GROUPS).flat();
export const STATUS_LABEL = {
  open: "Open",
  in_progress: "Active",
  client_in_progress: "In Progress",
  assigned: "Assigned",
  dev_done: "Dev Done",
  dev_testing: "Dev Testing",
  in_qa: "In QA",
  resolved: "Resolved",
  completed: "Completed",
  reopened: "Reopened",
  client_confirmation_pending: "Client Confirmation Pending",
  reference_pending: "Reference Pending",
  on_hold: "Hold",
  invalid: "No Issue (Invalid)",
};

export const VISIBLE_TICKET_DISPLAY_STATUSES = new Set([
  "open",
  "in_progress",
  "client_in_progress",
  "assigned",
  "dev_done",
  "dev_testing",
  "in_qa",
  "resolved",
  "completed",
  "reopened",
  "client_confirmation_pending",
  "reference_pending",
  "on_hold",
  "invalid",
]);

export function getTicketDisplayStatus(ticket) {
  const rawStatus = ticket?.status;
  if (!rawStatus) return null;
  const status = String(rawStatus).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return VISIBLE_TICKET_DISPLAY_STATUSES.has(status) ? status : null;
}

export function getClientDisplayStatus(ticket) {
  const status = getTicketDisplayStatus(ticket);
  if (!status) return null;
  if (["open", "reference_pending", "client_confirmation_pending"].includes(status)) {
    return status;
  }
  if (["completed", "invalid"].includes(status)) return "completed";
  return "client_in_progress";
}

const CLIENT_IN_PROGRESS_STATUSES = [
  "assigned",
  "dev_done",
  "dev_testing",
  "in_progress",
  "in_qa",
  "on_hold",
  "reopened",
  "resolved",
].sort();

export function groupClientStatusCounts(rows = []) {
  const groups = {
    open: {
      name: STATUS_LABEL.open,
      displayStatus: "open",
      value: 0,
      statuses: new Set(["open"]),
    },
    client_in_progress: {
      name: STATUS_LABEL.client_in_progress,
      displayStatus: "client_in_progress",
      value: 0,
      statuses: new Set(CLIENT_IN_PROGRESS_STATUSES),
    },
    client_confirmation_pending: {
      name: STATUS_LABEL.client_confirmation_pending,
      displayStatus: "client_confirmation_pending",
      value: 0,
      statuses: new Set(["client_confirmation_pending"]),
    },
    reference_pending: {
      name: STATUS_LABEL.reference_pending,
      displayStatus: "reference_pending",
      value: 0,
      statuses: new Set(["reference_pending"]),
    },
    completed: {
      name: STATUS_LABEL.completed,
      displayStatus: "completed",
      value: 0,
      statuses: new Set(["completed", "invalid"]),
    },
  };
  rows.forEach((row) => {
    const displayStatus = getClientDisplayStatus({ status: row._id });
    if (!displayStatus || !VISIBLE_TICKET_DISPLAY_STATUSES.has(displayStatus)) return;
    const group = groups[displayStatus];
    if (!group) return;
    group.value += row.count || 0;
    if (row._id) group.statuses.add(row._id);
  });
  return Object.values(groups)
    .map((group) => ({
      ...group,
      colorStatus: group.displayStatus,
      statuses: [...group.statuses],
      status: [...group.statuses].sort().join(","),
    }))
    .sort((left, right) => right.value - left.value);
}

export function groupStatusCounts(rows = []) {
  const bars = Object.fromEntries(ALL_STATUSES.map((status) => [status, {
    name: STATUS_LABEL[status] || status.replace(/_/g, " "),
    value: 0,
    statuses: new Set([status]),
    colorStatus: status,
  }]));
  rows.forEach((row) => {
    const status = getTicketDisplayStatus({ status: row._id });
    if (!status || !VISIBLE_TICKET_DISPLAY_STATUSES.has(status) || !bars[status]) return;
    const bar = bars[status];
    bar.value += row.count || 0;
    if (row._id) bar.statuses.add(row._id);
  });
  return Object.values(bars)
    .sort((left, right) => right.value - left.value)
    .map((bar) => {
      const statuses = [...bar.statuses];
      return { ...bar, statuses, status: statuses.sort().join(",") };
    });
}

const statusFilterOption = (label, status) => ({ label, value: status });

export const STATUS_FILTER_OPTIONS = [
  statusFilterOption("Open", "open"),
  statusFilterOption("Active", "in_progress"),
  statusFilterOption("Assigned", "assigned"),
  statusFilterOption("Dev Done", "dev_done"),
  statusFilterOption("Dev Testing", "dev_testing"),
  statusFilterOption("In QA", "in_qa"),
  statusFilterOption("Resolved", "resolved"),
  statusFilterOption("Completed", "completed"),
  statusFilterOption("Reopened", "reopened"),
  statusFilterOption("Client Confirmation Pending", "client_confirmation_pending"),
  statusFilterOption("Reference Pending", "reference_pending"),
  statusFilterOption("Hold", "on_hold"),
  statusFilterOption("No Issue (Invalid)", "invalid"),
];

export const TERMINAL_STATUSES = new Set(["completed", "invalid"]);

export const CLIENT_STATUS_FILTER_OPTIONS = [
  { label: "Open", value: "open" },
  {
    label: "In Progress",
    value: CLIENT_IN_PROGRESS_STATUSES.join(","),
  },
  { label: "Client Confirmation Pending", value: "client_confirmation_pending" },
  { label: "Reference Pending", value: "reference_pending" },
  { label: "Completed", value: ["completed", "invalid"].sort().join(",") },
];

export const STATUS_TONE = {
  open: "blue",
  in_progress: "cyan",
  client_in_progress: "cyan",
  assigned: "blue",
  dev_done: "gold",
  dev_testing: "purple",
  in_qa: "purple",
  resolved: "green",
  completed: "green",
  reopened: "blue",
  client_confirmation_pending: "amber",
  reference_pending: "amber",
  on_hold: "slate",
  invalid: "red",
};
export const PRIORITY_TONE = {
  P1: "red",
  P2: "orange",
  P3: "amber",
  P4: "blue",
  P5: "slate",
};

export const LOGO_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_3f4e74cb-e55c-426c-bea9-447f28885dc0/artifacts/fmya3g89_logo.png";
